import React, { useEffect, useState } from 'react';

// helpers
import useQueryParam from 'hooks/useQueryParam';
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { StateModel } from 'redux/reducers';
import { colorsTheme } from 'resources/theme/styled/colors';
import { setIntakeFormId } from 'redux/actions/intakeForm';
import { useDispatch, useSelector } from 'react-redux';
import { useEntityIntakeFormContext } from 'modules/IntakeForm/EntityIntakeForm/EntityIntakeFormContext';

// components
import Funding from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/Funding';
import Account from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/Account';
import BasicInfo from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/BasicInfo';
import EntityType from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/EntityType';
import EntityProfile from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/EntityProfile';
import LoadingWrapper from 'components/WrapperComponents/LoadingWrapper';
import ServiceProviders from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/ServiceProviders';
import AdditionalQuestions from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/AdditionalQuestions';
import CustomersAndProducts from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/CustomersAndProducts';
import AssociatedIndividuals from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/AssociatedIndividuals';
import SupplementaryQuestions from 'modules/IntakeForm/EntityIntakeForm/IntakeForm/SupplementaryQuestions';
import { Collapse, Divider, Panel, Text } from '@ui';

const ApplicationDetails = () => {
  const { t } = useTranslation('onboarding');
  const applicationId = useQueryParam({ param: 'id' });
  const dispatch = useDispatch();
  const intakeFormId = useSelector<StateModel, string | null>(
    (state) => state.intakeForm.intakeFormId,
  );

  const [activeKeys, setActiveKeys] = useState<string[]>([
    'entity_type',
    'entity_details',
    'finances',
    'associates',
    'additional_questions',
    'supplementary_questions',
  ]);

  const { intakeForm, fetchAndStoreIntakeForm } = useEntityIntakeFormContext();

  useEffect(() => {
    if (applicationId) {
      dispatch(setIntakeFormId(applicationId));
    }

    if (intakeFormId) {
      fetchAndStoreIntakeForm();
    }
  }, [applicationId, intakeFormId]);

  return (
    <>
      <LoadingWrapper loading={!intakeForm}>
        <StyledCollapse
          ghost
          bordered={false}
          expandIconPosition="end"
          activeKey={activeKeys}
          onChange={setActiveKeys}
        >
          <Panel
            key="entity_type"
            header={
              <Text
                variant="h4"
                weight="semi-bold"
                color={colorsTheme.colorWhite}
              >
                {t('intake_form.entity_type.title')}
              </Text>
            }
          >
            <EntityType disabled renderEntityTypeUsingSelect />
          </Panel>
          <StyledDivider />
          <Panel
            key="entity_details"
            header={
              <Text
                variant="h4"
                weight="semi-bold"
                color={colorsTheme.colorWhite}
              >
                {t('intake_form.entity_details.title')}
              </Text>
            }
          >
            <BasicInfo disabled />
            <Divider />
            <EntityProfile disabled />
            <Divider />
            <CustomersAndProducts disabled />
          </Panel>
          <StyledDivider />
          <Panel
            key="finances"
            header={
              <Text
                variant="h4"
                weight="semi-bold"
                color={colorsTheme.colorWhite}
              >
                {t('intake_form.finances.title')}
              </Text>
            }
          >
            <Funding disabled />
            <Divider />
            <Account disabled />
          </Panel>
          <StyledDivider />
          <Panel
            key="associates"
            header={
              <Text
                variant="h4"
                weight="semi-bold"
                color={colorsTheme.colorWhite}
              >
                {t('intake_form.associates.title')}
              </Text>
            }
          >
            <AssociatedIndividuals disabled />
            <Divider />
            <ServiceProviders disabled />
          </Panel>
          <StyledDivider />
          {intakeForm?.additionalQuestions &&
          intakeForm.additionalQuestions.length ? (
            <>
              <Panel
                key="additional_questions"
                header={
                  <Text
                    variant="h4"
                    weight="semi-bold"
                    color={colorsTheme.colorWhite}
                  >
                    {t('intake_form.additional_questions.title')}
                  </Text>
                }
              >
                <AdditionalQuestions disabled />
              </Panel>
              <StyledDivider />
            </>
          ) : null}
          {intakeForm?.triggeredSupplementaryQuestionForms?.length ? (
            <>
              <Panel
                key="supplementary_questions"
                header={
                  <Text
                    variant="h4"
                    weight="semi-bold"
                    color={colorsTheme.colorWhite}
                  >
                    {t('intake_form.supplementary_questions.title')}
                  </Text>
                }
              >
                {intakeForm?.triggeredSupplementaryQuestionForms.map((e) => (
                  <SupplementaryQuestions key={e._id} formId={e._id} disabled />
                ))}
              </Panel>
              <StyledDivider />
            </>
          ) : null}
        </StyledCollapse>
      </LoadingWrapper>
    </>
  );
};

const StyledCollapse = styled(Collapse)`
  border-block-start: none;

  .ant-collapse-header {
    align-items: center !important;
    padding: ${({ theme }) =>
      `${theme.paddingXl} ${theme.paddingMd} ${theme.paddingXl} 0`} !important;
  }

  .ant-collapse-content-box {
    padding-left: 0px !important;
    padding-top: 0px !important;
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0px;
`;

export default ApplicationDetails;
